import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Clock } from 'lucide-react';

const CALENDLY_URL = 'https://calendly.com/buildwithprima/30min';

const ThankYou = () => {
  const navigate = useNavigate();
  const calendlyContainerRef = useRef(null);
  const calendlyScriptInjected = useRef(false);

  useEffect(() => {
    // Fire Meta Pixel Lead conversion event
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'Lead');
    }
  }, []);

  // Listen for Calendly's "event scheduled" postMessage → redirect to /confirmation
  useEffect(() => {
    const handleCalendlyEvent = (e) => {
      if (e.data?.event === 'calendly.event_scheduled') {
        // Fire Pixel 'Schedule' with the Calendly invitee UUID as eventID so it
        // deduplicates with the server-side CAPI event from /api/calendly-webhook
        if (typeof window.fbq === 'function') {
          const inviteeUri = e.data?.payload?.invitee?.uri || '';
          const inviteeUuid = inviteeUri.split('/').pop();
          if (inviteeUuid) {
            window.fbq('track', 'Schedule', {}, { eventID: inviteeUuid });
          } else {
            window.fbq('track', 'Schedule');
          }
        }
        navigate('/confirmation');
      }
    };
    window.addEventListener('message', handleCalendlyEvent);
    return () => window.removeEventListener('message', handleCalendlyEvent);
  }, [navigate]);

  useEffect(() => {
    if (calendlyScriptInjected.current) return;
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);
    calendlyScriptInjected.current = true;
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#2C0616] px-4 py-12" data-testid="thank-you-page">
      <div className="max-w-4xl mx-auto text-center">
        {/* Success Icon */}
        <div className="flex justify-center mb-8">
          <div className="bg-[#220410] border border-[#C9A55A]/35 rounded-full p-6">
            <CheckCircle className="w-20 h-20 text-[#C9A55A]" />
          </div>
        </div>

        {/* Thank You Message */}
        <h1
          className="text-5xl md:text-6xl lg:text-7xl font-serif text-[#F5F0E8] mb-6 uppercase tracking-wide"
          style={{ fontWeight: 600 }}
          data-testid="thank-you-headline"
        >
          Thank You!
        </h1>

        <p className="text-2xl md:text-3xl font-sans text-[#F5F0E8] mb-10" style={{ fontWeight: 500 }}>
          Your application has been received
        </p>

        {/* 72-hour urgency banner */}
        <div
          className="bg-[#220410] text-[#F5F0E8] rounded-[16px] p-6 md:p-8 mb-8 max-w-3xl mx-auto"
          data-testid="urgency-banner"
        >
          <div className="flex items-center justify-center gap-3 mb-3">
            <Clock className="w-7 h-7 md:w-8 md:h-8 shrink-0" strokeWidth={2.5} />
            <h2
              className="text-2xl md:text-3xl lg:text-4xl font-serif uppercase tracking-wide"
              style={{ fontWeight: 600 }}
            >
              Book Your Call Within 72 Hours
            </h2>
          </div>
          <p className="text-base md:text-lg font-sans leading-relaxed">
            To lock your spot and get contacted, schedule your introduction call below within{' '}
            <strong>72 hours</strong>. Slots are limited and given on a first-come, first-served basis.
          </p>
        </div>

        {/* Calendly Inline Embed */}
        <div
          ref={calendlyContainerRef}
          className="calendly-inline-widget bg-[#2C0616] border-4 border-[#C9A55A]/35 rounded-[16px] overflow-hidden mb-10 mx-auto"
          data-url={`${CALENDLY_URL}?hide_event_type_details=1&hide_gdpr_banner=1&primary_color=0c4aad`}
          style={{ minWidth: '320px', height: '720px', maxWidth: '1000px' }}
          data-testid="calendly-embed"
        />

        {/* Fallback button (in case widget fails to load) */}
        <div className="mb-8">
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="prima-cta inline-block px-8 py-4 uppercase"
            style={{ fontWeight: 600 }}
            data-testid="calendly-fallback-link"
          >
            Or Open Calendar in New Tab →
          </a>
        </div>

        {/* Instagram CTA */}
        <div className="space-y-3 mb-6">
          <p className="text-base font-sans text-[#F5F0E8]/38">
            In the meantime, follow us for updates:
          </p>
          <a
            href="https://www.instagram.com/buildwithprima/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-[#F5F0E8] font-sans text-lg hover:underline"
            style={{ fontWeight: 500 }}
          >
            @buildwithprima
          </a>
        </div>

        {/* Back to Home */}
        <button
          onClick={() => navigate('/')}
          className="mt-4 text-[#F5F0E8] font-sans text-lg hover:underline"
          style={{ fontWeight: 500 }}
          data-testid="back-home"
        >
          ← Back to Home
        </button>
      </div>
    </div>
  );
};

export default ThankYou;
